"use client";

import { QueryClientProvider } from "@tanstack/react-query";
import { Provider as JotaiProvider } from "jotai";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { useEffect } from "react";
import { WebConvexProvider } from "@/components/providers/convex-provider";
import { webQueryClient } from "@/lib/query-client";

type PostHogClientWithExceptions = typeof posthog & {
  captureException?: (
    error: Error,
    properties?: Record<string, unknown>,
  ) => void;
};

function isLikelyPostHogNoise(
  message: string,
  stack: string,
  host: string,
): boolean {
  return (
    (message.includes("[PostHog.js]") && message.includes("RemoteConfig")) ||
    ((message.includes("Failed to fetch") ||
      message.includes("NetworkError")) &&
      (message.includes("posthog") ||
        message.includes(host) ||
        stack.includes("posthog") ||
        stack.includes("posthog-js")))
  );
}

// Browser-internal noise that is not actionable and pollutes Error Tracking
// (9 duplicate ResizeObserver issues dominated the issue list before this).
// Mirrors the Sentry ignoreErrors entry for the same messages.
const IGNORED_EXCEPTION_SNIPPETS = ["ResizeObserver loop"];

function isIgnoredExceptionEvent(event: {
  event?: string;
  properties?: Record<string, unknown>;
}): boolean {
  if (event.event !== "$exception") return false;
  try {
    const listJson = JSON.stringify(event.properties?.$exception_list ?? "");
    return IGNORED_EXCEPTION_SNIPPETS.some((s) => listJson.includes(s));
  } catch {
    return false;
  }
}

function captureClientException(
  error: Error,
  properties: Record<string, unknown>,
): void {
  const posthogWithExceptions = posthog as PostHogClientWithExceptions;
  if (typeof posthogWithExceptions.captureException === "function") {
    posthogWithExceptions.captureException(error, properties);
    return;
  }

  posthog.capture("client_exception", {
    message: error.message,
    stack: error.stack,
    ...properties,
  });
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Only initialize PostHog on the client side
    if (typeof window === "undefined") return;
    const enableAnalyticsInDev =
      process.env.NEXT_PUBLIC_ENABLE_ANALYTICS_IN_DEV === "true";
    if (process.env.NODE_ENV === "development" && !enableAnalyticsInDev) {
      try {
        posthog.opt_out_capturing();
        posthog.stopSessionRecording?.();
      } catch {
        // no-op
      }
      return;
    }

    // Check if PostHog is configured
    if (
      !process.env.NEXT_PUBLIC_POSTHOG_KEY ||
      !process.env.NEXT_PUBLIC_POSTHOG_HOST
    ) {
      if (process.env.NODE_ENV === "development") {
        console.warn(
          "PostHog not configured: Missing NEXT_PUBLIC_POSTHOG_KEY or NEXT_PUBLIC_POSTHOG_HOST",
        );
      }
      return;
    }

    // Ensure host URL doesn't have trailing slash
    const host = process.env.NEXT_PUBLIC_POSTHOG_HOST.replace(/\/$/, "");

    // Handle unhandled promise rejections from PostHog (common with ad blockers)
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const message = reason?.message || String(reason) || "";
      const stack = reason?.stack || "";

      if (isLikelyPostHogNoise(message, stack, host)) {
        // Suppress only known PostHog/ad-blocker network noise.
        event.preventDefault();
        return;
      }

      const error =
        reason instanceof Error
          ? reason
          : new Error(
              message || "Unhandled promise rejection without error object",
            );
      captureClientException(error, {
        source: "window.unhandledrejection",
      });
    };

    const handleWindowError = (event: ErrorEvent) => {
      const message =
        event.message ||
        (event.error instanceof Error ? event.error.message : "");
      const stack = event.error instanceof Error ? event.error.stack || "" : "";
      if (isLikelyPostHogNoise(message, stack, host)) {
        return;
      }

      const error =
        event.error instanceof Error
          ? event.error
          : new Error(message || "Window error without error object");
      captureClientException(error, {
        source: "window.error",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleWindowError);

    // Initialize PostHog with all main features enabled
    // Following official PostHog Next.js documentation
    try {
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
        api_host: host,
        // Required when api_host is a managed reverse proxy (dpa.multic.com):
        // resolves the toolbar and "view in PostHog" links back to the real UI.
        ui_host: "https://us.posthog.com",
        // Enable autocapture for automatic event tracking
        autocapture: true,
        // Explicitly enable client-side exception capture.
        capture_exceptions: true,
        // Capture pageviews on history changes (Next.js App Router compatible)
        capture_pageview: "history_change",
        // Capture page leaves if pageviews are captured
        capture_pageleave: "if_capture_pageview",
        // Enable session recording
        disable_session_recording: false,
        // Person profiles configuration
        person_profiles: "identified_only",
        // Cross-subdomain tracking: Share identity across www.multic.com and studio.multic.com
        cross_subdomain_cookie: true,
        // Use localStorage+cookie for better cross-domain persistence
        persistence: "localStorage+cookie",
        // Send session IDs in requests to backend for server-side event correlation
        // This adds X-PostHog-Session-Id header to requests to these domains
        __add_tracing_headers: ["studio.multic.com", "www.multic.com"],
        // Drop browser-noise exceptions (ResizeObserver loop) before ingest —
        // capture_exceptions:true captures them via posthog-js's own handler,
        // so the window.error filter above cannot intercept them.
        before_send: (event) =>
          event && isIgnoredExceptionEvent(event) ? null : event,
        // Debug mode in development
        debug: process.env.NODE_ENV === "development",
        // Handle PostHog loaded callback
        loaded: (_posthog) => {
          if (process.env.NODE_ENV === "development") {
            console.log("PostHog initialized successfully");
          }
        },
      });
    } catch (error) {
      // Log error but don't crash the app
      // Only log if it's not a fetch error (likely from ad blocker)
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      if (!errorMessage.includes("Failed to fetch")) {
        console.error("PostHog initialization error:", error);
      }
    }

    // Cleanup listeners
    return () => {
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
      window.removeEventListener("error", handleWindowError);
    };
  }, []);

  // IMPORTANT: Always render the same component tree structure to prevent
  // "Rendered more hooks than during the previous render" errors.
  // The posthog singleton is safe to pass before init() — PHProvider just
  // makes it available via React context. Conditional wrapping (with/without
  // PHProvider) caused tree structure changes that broke React's hook ordering
  // during client-side navigations (Fixes MULTIC-BACKEND-PROD-1H).
  return (
    <JotaiProvider>
      <QueryClientProvider client={webQueryClient}>
        <WebConvexProvider>
          <PHProvider client={posthog}>{children}</PHProvider>
        </WebConvexProvider>
      </QueryClientProvider>
    </JotaiProvider>
  );
}
